/**
 * giftCardsModel — pure display derivations for the Customers › Gift cards
 * surface (BON-223), framework-free so the list, detail and ledger all read
 * the same labels and the math is unit-testable on its own.
 *
 * Two deliberate departures from the design prototype, each settled:
 *   - search/status/source filtering happens SERVER-SIDE (the frozen contract
 *     takes them as query params), so there is no client-side match function
 *     here — only the query-building lives in `useGiftCardListState`;
 *   - real card codes are ALPHANUMERIC, not digits, so copy says "last 5
 *     characters" rather than the design's "last 5 digits".
 */
import type {
  GiftCardLedgerEntry,
  GiftCardListRow,
  GiftCardSource,
  GiftCardTransactionType,
} from './giftCardTypes';

/** The full number when the viewer may see it, otherwise "•••• {codeMasked}". */
export function cardNumberLabel(card: Pick<GiftCardListRow, 'code' | 'codeMasked'>): string {
  return card.code ?? `•••• ${card.codeMasked}`;
}

export const GIFT_CARD_SOURCE_LABEL: Record<GiftCardSource, string> = {
  FAREHARBOR_IMPORT: 'FareHarbor import',
  REFUND: 'Refund',
  NATIVE: 'Native',
  STAFF_ISSUED: 'Staff issued',
};

export function sourceLabel(source: GiftCardSource): string {
  return GIFT_CARD_SOURCE_LABEL[source];
}

export const GIFT_CARD_STATUS_OPTIONS: { value: 'all' | 'ACTIVE' | 'VOIDED'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'VOIDED', label: 'Voided' },
];

export const GIFT_CARD_SOURCE_OPTIONS: { value: 'all' | GiftCardSource; label: string }[] = [
  { value: 'all', label: 'All' },
  ...(Object.keys(GIFT_CARD_SOURCE_LABEL) as GiftCardSource[]).map((value) => ({
    value,
    label: GIFT_CARD_SOURCE_LABEL[value],
  })),
];

/** Icon KEY, not a component — this module stays framework-free. The ledger
 *  card resolves the key to a lucide icon. */
export type GiftCardTxnIcon = 'download' | 'wallet' | 'plus' | 'ticket' | 'pencil' | 'x' | 'clock';

export interface GiftCardTxnMeta {
  label: string;
  icon: GiftCardTxnIcon;
}

/** How each ledger row reads. RELOAD and EXPIRE are reserved and unused at
 *  launch (no reload or expiry path exists yet) but are mapped anyway — the
 *  enum is closed, so every member has a real label rather than a fallback. */
export const GIFT_CARD_TXN_META: Record<GiftCardTransactionType, GiftCardTxnMeta> = {
  ISSUE_IMPORT: { label: 'Issued from import', icon: 'download' },
  ISSUE_REFUND: { label: 'Issued from refund', icon: 'wallet' },
  ISSUE_STAFF: { label: 'Issued by staff', icon: 'plus' },
  RELOAD: { label: 'Reloaded', icon: 'plus' },
  REDEEM: { label: 'Redeemed on booking', icon: 'ticket' },
  ADJUSTMENT: { label: 'Adjustment', icon: 'pencil' },
  VOID: { label: 'Voided', icon: 'x' },
  EXPIRE: { label: 'Expired', icon: 'clock' },
};

export function txnMeta(type: GiftCardTransactionType): GiftCardTxnMeta {
  return GIFT_CARD_TXN_META[type];
}

/**
 * The balance held immediately after each entry, aligned to the ledger's own
 * newest-first order (index 0 = the balance today).
 *
 * Entries are SIGNED, so this is a running sum from the oldest entry forward;
 * computed here as a suffix sum over the newest-first array, which is the same
 * arithmetic without a reverse-map-reverse round trip.
 */
export function runningBalances(entries: Pick<GiftCardLedgerEntry, 'amountCents'>[]): number[] {
  const out = new Array<number>(entries.length);
  let sum = 0;
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    sum += entries[i].amountCents;
    out[i] = sum;
  }
  return out;
}

export interface GiftCardCreditsDebits {
  creditedCents: number;
  debitedCents: number;
}

/** Total credited (positive entries) and debited (negative entries, as a
 *  positive figure) — the "$X credited · $Y debited" line under the balance. */
export function creditsAndDebitsCents(
  entries: Pick<GiftCardLedgerEntry, 'amountCents'>[],
): GiftCardCreditsDebits {
  let creditedCents = 0;
  let debitedCents = 0;
  for (const e of entries) {
    if (e.amountCents > 0) creditedCents += e.amountCents;
    else debitedCents += -e.amountCents;
  }
  return { creditedCents, debitedCents };
}

export interface GiftCardListQuery {
  search: string;
  status: 'all' | 'ACTIVE' | 'VOIDED';
  source: 'all' | GiftCardSource;
}

/** Whether the toolbar's Clear button should show, and which empty-state copy
 *  applies — true whenever the query differs from its defaults. */
export function isGiftCardFiltered(query: GiftCardListQuery): boolean {
  return query.search.trim() !== '' || query.status !== 'all' || query.source !== 'all';
}

export interface GiftCardEmptyCopy {
  title: string;
  body: string;
}

/** No-results copy: quote the search term when one was typed, name "filters"
 *  generically when the narrowing came only from the dropdowns, and read as a
 *  true empty state when nothing is filtered at all. */
export function giftCardEmptyCopy(search: string, filtered: boolean): GiftCardEmptyCopy {
  const trimmed = search.trim();
  if (trimmed !== '') {
    return {
      title: `No gift cards match “${trimmed}”`,
      body: 'Try a customer name, an email address, the full card number, or its last 5 characters.',
    };
  }
  if (filtered) {
    return {
      title: 'No gift cards match these filters',
      body: 'Try a customer name, an email address, the full card number, or its last 5 characters.',
    };
  }
  return {
    title: 'No gift cards yet',
    body: 'Imported and issued gift cards will appear here.',
  };
}

const EM_DASH = '—';

/** House-format calendar date, e.g. `Aug 9, 2026` — date only, no time: a
 *  card's `createdAt` is a migration-batch or issuance date, not a moment
 *  anyone needs to the minute. */
export function formatCreatedDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "1 gift card" / "N gift cards" — pluralized on the API's total, which is
 *  the count across ALL pages, not just the rows rendered on this one. */
export function giftCardCountLabel(total: number): string {
  return `${total} ${total === 1 ? 'gift card' : 'gift cards'}`;
}

/** "N records · newest first" — the ledger card's subheading. */
export function ledgerRecordsLabel(count: number): string {
  return `${count} ${count === 1 ? 'record' : 'records'} · newest first`;
}
